/**
 * Graph palette definitions for JDiskMark charts.
 *
 * Single source of truth — imported by BenchmarkDetailsScreen,
 * LiveBenchmarkDemo, and DriveDetailsScreen.
 *
 * Standalone palettes live in ./palettes/.
 * Linked palettes are defined inside their theme files in ./themes/.
 * Only standalone palettes appear in the palette dropdown.
 */
import { FEATURE_FLAGS } from './featureFlags';

// ── Standalone palettes ─────────────────────────────────────────────────
import classicLight from './palettes/classicLight';
import classicDark  from './palettes/classicDark';
import ember        from './palettes/ember';
import lagoon       from './palettes/lagoon';
import marine       from './palettes/marine';
import betaDark     from './palettes/betaDark';
import betaLight    from './palettes/betaLight';

// ── Linked palettes (defined in theme files, not selectable standalone) ─
import { palette as oldGloryPalette }     from './themes/oldGlory';
import { palette as sakuraPalette }       from './themes/sakura';
import { palette as harvestPalette }      from './themes/harvest';
import { palette as trickOrTreatPalette } from './themes/trickOrTreat';
import { palette as yuletidePalette }     from './themes/yuletide';

// ── All palettes (standalone + linked) for lookup ───────────────────────
const ALL_PALETTES = [
  classicLight, classicDark, ember, lagoon, marine, betaDark, betaLight,
  oldGloryPalette, sakuraPalette, harvestPalette, trickOrTreatPalette, yuletidePalette,
];

// ── Only standalone palettes for the dropdown ───────────────────────────
const STANDALONE_PALETTES = [
  classicLight, classicDark, ember, lagoon, marine, betaDark, betaLight,
];

// ── localStorage key migration (old name → new palette ID) ──────────────
const MIGRATION_MAP = {
  Light:      'classicLight',
  Dark:       'classicDark',
  WARM:       'ember',
  BLUE_GREEN: 'lagoon',
  COOL:       'marine',
};

const STORAGE_KEY = 'graphPalette';

/**
 * Read the saved palette ID from localStorage, migrating old keys if needed.
 */
export function getSavedPaletteId(fallback = 'classicLight') {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return fallback;

  if (MIGRATION_MAP[raw]) {
    const newId = MIGRATION_MAP[raw];
    localStorage.setItem(STORAGE_KEY, newId);
    return newId;
  }

  return raw;
}

/**
 * Persist the selected palette ID to localStorage.
 */
export function savePaletteId(id) {
  localStorage.setItem(STORAGE_KEY, id);
}

// ── Feature-flag gate ───────────────────────────────────────────────────
const PALETTE_FLAG_MAP = {
  betaDark:  'palette_betaDark',
  betaLight: 'palette_betaLight',
};

function isPaletteEnabled(entry) {
  if (entry.enabled) return true;
  const flagKey = PALETTE_FLAG_MAP[entry.id];
  return flagKey ? FEATURE_FLAGS[flagKey] === true : false;
}

// ── Flatten palette for Chart.js consumption ────────────────────────────
function flattenPalette(entry) {
  return {
    ...entry.graph,
    chartBg:   entry.canvas?.chartBg   ?? '#2B2B2B',
    gridColor: entry.canvas?.gridColor ?? '#444444',
    textColor: entry.canvas?.textColor ?? '#ADB5BD',
  };
}

/**
 * Ordered list of enabled standalone palettes for dropdown rendering.
 * Linked palettes are NOT included — they auto-apply with their theme.
 */
export const PALETTE_LIST = STANDALONE_PALETTES
  .filter(isPaletteEnabled)
  .map((entry) => ({
    id:          entry.id,
    displayName: entry.displayName,
    colors:      flattenPalette(entry),
  }));

/**
 * Full palette lookup map (includes linked palettes for theme auto-switch).
 */
export const PALETTES = Object.fromEntries(
  ALL_PALETTES.map((entry) => [entry.id, flattenPalette(entry)]),
);

/**
 * Returns the flat palette colors for the given ID.
 */
export function getPalette(id) {
  return PALETTES[id] ?? PALETTES.classicLight;
}

export const DEFAULT_LIGHT_PALETTE = 'classicLight';
export const DEFAULT_DARK_PALETTE  = 'classicDark';
